import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Popover, notification, Button, Tooltip, Tag, Modal, message, Space } from "antd";
import {
  CheckOutlined,
  MinusOutlined,
  FilePdfFilled,
  InfoCircleOutlined,
  QuestionOutlined,
  CloseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

import { formatDateTime } from "../../helpers/date";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import config from "../../config/app"

import { PaymentGatewayTag } from "../../constants/paymentGateways";

const downloadReport = (payoutId) => {
  axios
    .post(`/api/payout/${payoutId}/advice-report`, {})
    .then((response) => {
      window.open(response.data.url);
    })
    .catch(async (err) => {
      notification.error({
        message: `Error when downloading Payout Report`,
        description: err.response?.data.message || err.message,
      });
    });
};

const cancelPayout = (id) => {

  Modal.confirm({
    title: `Are you sure to cancel the payout  ${id}?`,
    onOk() {
      axios
        .post(`/api/payout/${id}/cancel`, {})
        .then((res) => {
          message.info("The bulk parcel cancelation process is done");
        })
        .catch((error) => {
          message.error(error.response.data.message || error.message);
        });
    },
  });
};

const PayoutList = (props) => {
  const { permissions } = usePermissions();

  const [payouts, setPayouts] = useState(props.payouts);
  const [selectedRows, setSelectedRows] = useState(props.selectedRows);

  useEffect(() => {
    setPayouts(props.payouts);
  }, [props.payouts]);

  const table = [
    {
      title: "Payout ID",
      dataIndex: ["objectId"],
      width: 120,
      ellipsis: true,
    },
    {
      title: "Withdrawal",
      dataIndex: "requestedBy",
      width: 110,
      render: (requestedBy) => {
        if (requestedBy) {
          return <CheckOutlined style={{ color: "green" }} />;
        }

        return <span></span>;
      },
    },
    {
      title: "Payment Provider",
      dataIndex: ["paymentProvider"],
      width: 110,
      render: (paymentProvider) => {
        if (paymentProvider) {
          return PaymentGatewayTag(paymentProvider)
        }
      },
    },
    {
      title: "Remark",
      dataIndex: ["remark"],
      width: 150,
      ellipsis: true,
    },
    {
      title: "Beneficiary Merchant",
      dataIndex: ["user"],
      render: (user, payout) => {
        const bankDetails = (
          <p>
            <strong>Bank</strong>: {payout?.payload?.bankName}
            <br />
            <strong>Bank Account</strong>: {payout?.payload?.accountNumber}
            <br />
            <strong>Account Name</strong>: {payout?.payload?.accountName}
            <br />
          </p>
        );

        return (
          <>
            {`${user.countryCode || ""}${user.phone || ""} (${user.email || ""
              }) `}
            <Popover content={bankDetails}>
              <InfoCircleOutlined />
            </Popover>
          </>
        );
      },
    },
    {
      title: "Requested Total (RM)",
      dataIndex: "requestAmount",
      width: 180,
      render: (requestedBy, payout) => {
        if (!requestedBy) {
          return "-"
        }

        const earlyCharge = -(payout.requestAmount * (config.earlyWithdrawalRate / 100))
        const totalDetails = (
          <p>
            <strong>COD Total</strong>: {payout.requestAmount.toFixed(2)}
            <br />
            <strong>Early Withdrawal Charge</strong>: {earlyCharge.toFixed(2)}
          </p>
        );

        return (
          <>
            {(payout.requestAmount + earlyCharge).toFixed(2)} ({payout.requestOrderIds.length}{" "}
            Orders){` `}
            <Popover content={totalDetails}>
              <InfoCircleOutlined />
            </Popover>
          </>
        );
      },
    },
    {
      title: "Total (RM)",
      dataIndex: "amount",
      width: 170,
      render: (amount, payout) => {
        if (!amount) {
          return "-"
        }

        const totalDetails = (
          <p>
            <strong>COD Total</strong>: {amount}
            <br />
            <strong>Early Withdrawal Charge</strong>: {payout?.earlyChargeValue || "-"}
            <br />
            <strong>Adjustment</strong>: {payout?.adjustmentValue || "-"}
            <br />
          </p>
        );

        return (
          <>
            {(payout.grandTotal || amount || 0).toFixed(2)} ({payout.orderIds.length}{" "}
            Orders){` `}
            <Popover content={totalDetails}>
              <InfoCircleOutlined />
            </Popover>
          </>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      width: 150,
      render: (createdAt) => formatDateTime(createdAt),
    },
    {
      title: "Success Date",
      dataIndex: "successAt",
      width: 150,
      render: (text) => text ? formatDateTime(text) : "-",
    },
    {
      title: "Status",
      dataIndex: "successAt",
      width: 80,
      render: (successAt, payout) => {

        if (payout.isCancelled) {
          return (
            <Tooltip title={payout.isCancelled}>
              <Tag color={"yellow"}>Cancelled</Tag>
            </Tooltip>
          );
        }

        if (successAt) {
          return <Tag color={"green"}>Success</Tag>;
        }

        if (payout.error) {
          return (
            <Tooltip title={payout.error}>
              <Tag color={"red"}>Failed</Tag>
            </Tooltip>
          );
        }

        return <Tag color={"yellow"}>Pending</Tag>;
      },
    },
    {
      title: "Email",
      dataIndex: "isEmailSent",
      width: 70,
      render: (isEmailSent) => {
        if (isEmailSent) {
          return <Tag color={"green"}>Yes</Tag>;
        }

        return <Tag color={"red"}>No</Tag>;
      },
    },
    {
      title: "Transaction Number",
      dataIndex: "ref_no",
      width: 220,
    },
    {
      title: "Action",
      dataIndex: "objectId",
      minWidth: 100,
      render: (id, payout) => {
        const actions = []

        if (payout.successAt && permissions?.includes(PERMISSIONS.DOWNLOAD_PAYMENT_ADVICE)) {
          actions.push(
            <Tooltip key="advice" title="Payment Advice">
              <Button onClick={() => downloadReport(id)}>
                <FilePdfFilled />
              </Button>
            </Tooltip>
          )
        }

        if (!payout.successAt && !payout.error && permissions?.includes(PERMISSIONS.CANCEL_PAYOUT) && !payout.isCancelled && payout.paymentProvider === "paydibs") {
          actions.push(
            <Tooltip key="cancel" title="Cancel Payout">
              <Button danger onClick={() => cancelPayout(id)}>
                Cancel
              </Button>
            </Tooltip>
          )
        }

        if (
          !payout.successAt &&
          !payout.error &&
          !payout.requestedBy &&
          !payout.isCancelled &&
          permissions?.includes(PERMISSIONS.UPDATE_REMITTANCE_STATUS)
        ) {
          actions.push(
            <Tooltip key="status" title="Check Status">
              <a target="_blank" rel="noreferrer" href={`/payout/status/${id}`}>
                <Button>
                  <QuestionOutlined />
                </Button>
              </a>
            </Tooltip>
          )
        }

        if (
          !payout.successAt &&
          !payout.error &&
          payout.requestedBy &&
          permissions?.includes(PERMISSIONS.PROCESS_WITHDRAWAL)
        ) {
          actions.push(
            <Tooltip key="withdrawal" title="Process Withdrawal">
              <a target="_blank" rel="noreferrer" href={`/payout/withdrawal/${id}`}>
                <Button>
                  <PlayCircleOutlined />
                </Button>
              </a>
            </Tooltip>
          )
        }

        if (!actions.length) return null

        return (
          <Space size="small">
            {actions}
          </Space>
        )
      }
    }
  ];

  return (
    <div className="table">
      <Table
        rowSelection={{ selectedRows, onChange: props.onSelectRows }}
        rowKey="objectId"
        className="table-section"
        columns={table}
        dataSource={payouts}
        pagination={false}
        scroll={{ x: 1800, y: 600 }}
        loading={props.loading}
      />
    </div>
  );
};

export default PayoutList;
